import { useFastRefreshFixer } from 'src/hooks/dev/useFastRefreshFixer';
import React, { FC, useEffect, useRef, useState, useMemo } from 'react';
import { TrackedLink } from 'src/components/TrackedLink';
import SwiperClass from 'swiper/types/swiper-class';
import {
  GenericSectionHeader,
  NavigationButton,
} from 'src/modules/materials/components/ShowcasePage/common';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Theme } from 'vendor/mods/styled-components';
import { darkTheme, dirRTL, redTheme } from 'src/styles/theme';
import { ChevronLeft } from 'src/components/icons/ChevronLeft';
import { ChevronRight } from 'src/components/icons/ChevronRight';
import styled, { css } from 'styled-components';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { Showcase } from 'src/modules/home/components/HomeBusinessCasesSection/Showcase';
import { media } from 'src/styles/designs';
import { useBusinessCasesVideosQuery } from 'src/modules/home/hooks/useBusinessCasesVideosQuery';
import { route } from 'src/services/route';
import range from 'lodash/range';
import { Skeleton } from 'src/components/common/Skeleton';
import { useIsRTL } from 'src/hooks/useIsRTL';
import { ShowcaseListRenderComponentProps } from 'src/modules/materials/models/showcase';

const businessCaseElement = css`
  width: 120px;
  height: 269px;

  ${media.w.greaterThanOrEqual.desktop} {
    width: 180px;
    height: 401px;
  }
`;

const VideoSwiper = styled(Swiper)`
  padding-left: var(--results-left-margin);
  padding-right: calc(var(--constraint-container-right-margin) - 20px);
  margin-bottom: 42px;

  ${dirRTL} {
    padding-right: var(--results-left-margin);
    padding-left: calc(var(--constraint-container-right-margin) - 20px);
  }

  ${media.w.greaterThanOrEqual.desktop} {
    margin-bottom: 40px;
  }

  .swiper-slide {
    position: relative;
    margin-right: 8px;
    background: ${({ theme }) => theme.palette.background};
    ${dirRTL} {
      margin-right: 0;
      margin-left: 8px;
    }

    ${businessCaseElement}
  }
`;

export const SmallerShowcase = styled(Showcase)`
  ${businessCaseElement}
`;

const SwiperArea = styled.div`
  position: relative;
  overflow: hidden;
  margin: 0 calc(-1 * var(--results-right-margin)) 0
    calc(-1 * var(--results-left-margin));

  ${dirRTL} {
    margin: 0 calc(-1 * var(--results-left-margin)) 0
      calc(-1 * var(--results-right-margin));
  }

  min-height: 269px;

  ${media.w.greaterThanOrEqual.desktop} {
    min-height: 401px;
  }
`;

const SwiperContent = styled.div`
  position: relative;
  ${NavigationButton} {
    opacity: 0;
  }
  &:hover > ${NavigationButton} {
    opacity: 1;
  }
`;

const StyledNavigationButton = styled(NavigationButton)`
  && {
    top: 50%;
    transform: translateY(-50%);
    margin-top: 0;
    margin-bottom: 0;
  }
`;

export const BusinessCasesContainer: FC<ShowcaseListRenderComponentProps> = ({
  section,
}) => {
  const key = useFastRefreshFixer();
  const prevRef = useRef<HTMLButtonElement>();
  const nextRef = useRef<HTMLButtonElement>();
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const { data, isLoading } = useBusinessCasesVideosQuery(10);

  const isRTL = useIsRTL();

  useEffect(() => {
    if (swiper && prevRef.current && nextRef.current && swiper.navigation) {
      swiper.navigation.init();
      swiper.navigation.update();
    }
  }, [swiper]);

  const translateItem = useTranslateItem();

  const preparedVideos = useMemo(() => {
    const videos = data?.map((video) => {
      const speaker = translateItem(video.speakers[0].people_id);

      return { speaker, link: route.video(video).page() };
    });

    return videos || [];
  }, [data, translateItem]);

  const hasContent = isLoading || preparedVideos.length > 0;

  return (
    <>
      <GenericSectionHeader section={section} />

      <SwiperArea key={String(isRTL) + key}>
        {hasContent && (
          <SwiperContent>
            <Theme theme={redTheme}>
              <VideoSwiper
                key={key}
                threshold={15}
                onSwiper={setSwiper}
                slidesPerView="auto"
                watchSlidesProgress
                navigation={{
                  prevEl: prevRef.current,
                  nextEl: nextRef.current,
                }}
                wrapperTag="ul"
              >
                {isLoading
                  ? range(5).map((num) => (
                      <SwiperSlide key={num} tag="li">
                        <Skeleton fill />
                      </SwiperSlide>
                    ))
                  : preparedVideos.map(({ link, speaker }) => {
                      return (
                        <SwiperSlide key={link} tag="li">
                          <TrackedLink href={link} passHref>
                            <SmallerShowcase
                              image={speaker.portrait?.id}
                              title={speaker.name}
                              subtitle={[speaker.degree, speaker.title]
                                .filter((el) => el)
                                .join(', ')}
                            />
                          </TrackedLink>
                        </SwiperSlide>
                      );
                    })}
              </VideoSwiper>
            </Theme>
            <Theme theme={darkTheme}>
              <StyledNavigationButton
                kind="circled"
                ref={prevRef}
                icon={ChevronLeft}
                $direction="left"
              />
              <StyledNavigationButton
                kind="circled"
                ref={nextRef}
                icon={ChevronRight}
                $direction="right"
              />
            </Theme>
          </SwiperContent>
        )}
      </SwiperArea>
    </>
  );
};
